/* ConstitutionalToolbox · GC64 · MP-11 (Feb 2026)
 * ================================================
 * The single canonical workshop drawer. The resolver owns search;
 * the drawer is transient UI, never a persisted landing-page choice.
 * Sep 2026: opening Tools must not replace the calendar URL with an
 * auto-open bookmark. Legacy toolbox query flags are removed without
 * changing the page, its other parameters or its hash.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { Menu, X, MessageCircle, LogOut } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL || "";
const CONVERSATION_ROUTE = "/talk";

export function ConstitutionalToolbox() {
  // Do not restore this from URLs or storage. Only an explicit Tools tap
  // may open the drawer; HomeRouter remains the calendar authority.
  const [open, setOpen] = useState(false);
  const [caps, setCaps] = useState([]);
  const [toolsError, setToolsError] = useState(false);
  const [toolsLoading, setToolsLoading] = useState(false);
  const [retry, setRetry] = useState(0);
  const [query, setQuery] = useState("");
  const [resolveResult, setResolveResult] = useState(null);
  const debounceRef = useRef(null);
  const triggerRef = useRef(null);
  const searchRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const accountId = user?.id || user?._id || null;
  const persona = [user?.role, user?.is_admin, user?.admin_view_role, user?.operating_mode, user?.founder_mode_active_role].join(":");

  // A reused shell must not carry an open drawer to another route/account.
  // Replace (not push) old URLs, preserving OAuth/mail/booking parameters.
  useEffect(() => {
    setOpen(false);
    setQuery("");
    setResolveResult(null);
    const params = new URLSearchParams(location.search);
    if (params.has("toolbox")) {
      params.delete("toolbox");
      const remaining = params.toString();
      navigate({ pathname: location.pathname, search: remaining ? `?${remaining}` : "", hash: location.hash },
        { replace: true, state: location.state });
    }
  }, [accountId, location.key, location.pathname, location.search, location.hash, location.state, navigate]);

  // Safari can restore an entire page from its back/forward cache without
  // remounting React. Clear the transient overlay on pagehide and restore.
  useEffect(() => {
    const reset = () => setOpen(false);
    const restored = (event) => { if (event.persisted) reset(); };
    window.addEventListener("pagehide", reset);
    window.addEventListener("pageshow", restored);
    return () => {
      window.removeEventListener("pagehide", reset);
      window.removeEventListener("pageshow", restored);
    };
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    searchRef.current?.focus({ preventScroll: true });
    const onKeyDown = (event) => {
      if (event.key === "Escape") { event.preventDefault(); close(); }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, close]);

  // Full capability list — used when the search box is empty.
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    const controller = new AbortController();
    setCaps([]);
    setToolsLoading(true);
    setToolsError(false);
    (async () => {
      try {
        const r = await axios.get(`${API}/api/toolbox/capabilities`, {
          withCredentials: true, signal: controller.signal,
        });
        if (alive) setCaps(Array.isArray(r.data?.capabilities) ? r.data.capabilities : []);
      } catch { if (alive) { setCaps([]); setToolsError(true); } }
      finally { if (alive) setToolsLoading(false); }
    })();
    return () => { alive = false; controller.abort(); };
  }, [open, accountId, persona, retry]);

  // Cancel stale resolver calls when the drawer closes or the query changes.
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    const controller = new AbortController();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    setResolveResult(null);
    if (!q) { setResolveResult(null); return undefined; }
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/api/toolbox/resolve`, {
          params: { q }, withCredentials: true, signal: controller.signal,
        });
        if (alive) setResolveResult(r.data || null);
      } catch { if (alive) setResolveResult(null); }
    }, 250);
    return () => {
      alive = false;
      controller.abort();
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, open, accountId, persona]);

  const groups = useMemo(() => {
    if (!query.trim()) {
      const g = {};
      for (const c of caps) {
        g[c.group] = g[c.group] || [];
        g[c.group].push(c);
      }
      return g;
    }
    const items = [];
    if (resolveResult?.best) items.push(resolveResult.best.capability);
    for (const a of (resolveResult?.alternatives || [])) {
      if (!items.find((x) => x.id === a.capability.id)) items.push(a.capability);
    }
    if (items.length === 0) return {};
    return { "Best matches": items };
  }, [caps, query, resolveResult]);

  const go = useCallback((route) => {
    setOpen(false);
    // Keep the existing full-document destination handoff for stale iPad
    // SPA recovery. Opening/closing Tools itself no longer navigates.
    if (route) window.location.assign(route);
  }, []);

  const openConversation = useCallback((utterance) => {
    setOpen(false);
    navigate(`${CONVERSATION_ROUTE}?q=${encodeURIComponent(utterance)}`);
  }, [navigate]);

  const onEnter = useCallback(() => {
    const q = query.trim();
    if (!q) return;
    if (resolveResult?.best?.capability?.route) {
      go(resolveResult.best.capability.route);
      return;
    }
    if (resolveResult?.alternatives?.[0]?.capability?.route) {
      go(resolveResult.alternatives[0].capability.route);
      return;
    }
    openConversation(q);
  }, [query, resolveResult, go, openConversation]);

  const handleLogout = useCallback(async () => {
    setOpen(false);
    await logout();
    navigate("/login", { replace: true });
  }, [logout, navigate]);

  const showConversationHint = query.trim() && resolveResult && !resolveResult.best
    && (resolveResult.alternatives || []).length === 0;

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        onClick={() => setOpen(true)}
        data-testid="toolbox-trigger"
        aria-label="Open toolbox"
        aria-expanded={open}
        aria-controls="ndg-toolbox-drawer"
        className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md px-3 py-2 font-bold text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800"
      >
        <Menu className="w-4 h-4" aria-hidden="true" />
        <span>Tools</span>
      </button>
      {open && (
        <div
          id="ndg-toolbox-drawer"
          data-testid="toolbox-drawer"
          role="dialog"
          aria-label="NDG toolbox"
          className="inset-0 bg-white dark:bg-slate-950 z-30 flex flex-col"
          style={{ position: "fixed" }}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-800">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Toolbox</p>
            <button
              type="button"
              data-testid="toolbox-close"
              aria-label="Close toolbox"
              onClick={close}
              className="rounded-md px-2 py-1.5 text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800">
            <input
              ref={searchRef}
              data-testid="toolbox-search"
              aria-label="Search tools"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onEnter(); } }}
              placeholder="Ask for what you need…"
              className="w-full rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm"
            />
            {resolveResult?.best && (
              <p data-testid="toolbox-hint-best" className="mt-2 text-[11px] text-slate-500">
                Press Enter to open <span className="font-semibold text-slate-700 dark:text-slate-200">{resolveResult.best.capability.name}</span>
              </p>
            )}
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
            {toolsLoading ? <p role="status">Loading tools…</p> : toolsError ? (
              <div role="alert"><p>Tools could not be loaded.</p><button onClick={() => setRetry(n => n + 1)}>Retry</button></div>
            ) : showConversationHint ? (
              <div
                data-testid="toolbox-conversation-fallback"
                className="rounded-md border border-slate-200 dark:border-slate-700 p-4 text-sm text-slate-700 dark:text-slate-200"
              >
                <div className="flex items-center gap-2 mb-2 text-slate-500">
                  <MessageCircle className="w-4 h-4" />
                  <span className="text-[11px] uppercase tracking-wide font-bold">Ask NDG</span>
                </div>
                <p className="italic text-slate-600 dark:text-slate-300 mb-3">
                  Press Enter and I&apos;ll take that as a question.
                </p>
                <button
                  type="button"
                  data-testid="toolbox-open-assistant"
                  onClick={() => openConversation(query.trim())}
                  className="rounded-md bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-sm px-3 py-1.5"
                >Ask NDG</button>
              </div>
            ) : Object.keys(groups).length === 0 ? (
              <p data-testid="toolbox-empty" className="text-xs text-slate-500 italic">No capabilities match.</p>
            ) : (
              Object.entries(groups).map(([group, items]) => (
                <section key={group} data-testid={`toolbox-group-${group.toLowerCase().replace(/\s+/g, "-")}`}>
                  <h3 className="text-[10px] uppercase tracking-wide font-bold text-slate-400 mb-1">{group}</h3>
                  <ul className="space-y-1">
                    {items.map((c) => (
                      <li key={c.id}>
                        <button
                          type="button"
                          data-testid={`toolbox-cap-${c.id}`}
                          onClick={() => go(c.route)}
                          className="w-full text-left rounded-md px-2 py-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 text-sm text-slate-800 dark:text-slate-100"
                        >{c.name}</button>
                      </li>
                    ))}
                  </ul>
                </section>
              ))
            )}
          </div>
          <div className="border-t border-slate-200 dark:border-slate-800 px-4 pt-3 pb-safe">
            <button
              type="button"
              data-testid="toolbox-logout"
              onClick={handleLogout}
              className="w-full min-h-[48px] flex items-center justify-center gap-2 rounded-xl bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-300 font-bold text-base active:scale-[0.99]"
            >
              <LogOut className="w-5 h-5" aria-hidden="true" />
              Log out
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default ConstitutionalToolbox;
